import { CompetitionStatus, PaymentStatus, TeamRegistrationStatus } from '@shared/enums/domain.enum';

export interface StatusBadgeConfig {
  label: string;
  bg: string;
  text: string;
}

export interface TeamPaymentStatusConfig extends StatusBadgeConfig {
  isFreeCompetition: boolean;
}

// ─── Payment status ─────────────────────────────────────────────────────────

export const PAY_STATUS_CONFIG: Record<PaymentStatus, StatusBadgeConfig> = {
  [PaymentStatus.PENDING]: { label: 'En attente', bg: 'bg-amber-100', text: 'text-amber-700' },
  [PaymentStatus.COMPLETED]: { label: 'Payé', bg: 'bg-emerald-100', text: 'text-emerald-700' },
  [PaymentStatus.FAILED]: { label: 'Échoué', bg: 'bg-red-100', text: 'text-red-700' },
  [PaymentStatus.REFUNDED]: { label: 'Remboursé', bg: 'bg-purple-100', text: 'text-purple-700' },
  [PaymentStatus.PARTIALLY_REFUNDED]: { label: 'Remb. partiel', bg: 'bg-fuchsia-100', text: 'text-fuchsia-700' },
};

export const FREE_COMPETITION_PAYMENT_CONFIG: StatusBadgeConfig = {
  label: 'Gratuit',
  bg: 'bg-sky-100',
  text: 'text-sky-700',
};

const CANCELLED_PAYMENT_CONFIG: StatusBadgeConfig = {
  label: 'Sans objet',
  bg: 'bg-slate-100',
  text: 'text-slate-400',
};

export function getTeamPaymentStatusConfig(
  team: { paymentStatus: PaymentStatus; registrationStatus?: TeamRegistrationStatus; paidAmount?: number | null },
  competition?: { price?: number | null },
): TeamPaymentStatusConfig {
  if ((competition?.price ?? null) === 0) {
    return { ...FREE_COMPETITION_PAYMENT_CONFIG, isFreeCompetition: true };
  }

  // When registration is rejected/cancelled and never paid, payment status is irrelevant
  const isTerminated =
    team.registrationStatus === TeamRegistrationStatus.REJECTED ||
    team.registrationStatus === TeamRegistrationStatus.CANCELLED;
  if (isTerminated && team.paymentStatus === PaymentStatus.PENDING) {
    return { ...CANCELLED_PAYMENT_CONFIG, isFreeCompetition: false };
  }

  return {
    ...PAY_STATUS_CONFIG[team.paymentStatus],
    isFreeCompetition: false,
  };
}

// ─── Team registration status ───────────────────────────────────────────────

export const REG_STATUS_CONFIG: Record<TeamRegistrationStatus, StatusBadgeConfig> = {
  [TeamRegistrationStatus.PENDING]: { label: 'En attente', bg: 'bg-amber-100', text: 'text-amber-700' },
  [TeamRegistrationStatus.CONFIRMED]: { label: 'Confirmée', bg: 'bg-emerald-100', text: 'text-emerald-700' },
  [TeamRegistrationStatus.REJECTED]: { label: 'Rejetée', bg: 'bg-red-100', text: 'text-red-700' },
  [TeamRegistrationStatus.CANCELLED]: { label: 'Annulée', bg: 'bg-slate-100', text: 'text-slate-600' },
};

// ─── Competition status ─────────────────────────────────────────────────────

export const COMP_STATUS_CONFIG: Record<CompetitionStatus, StatusBadgeConfig> = {
  [CompetitionStatus.DRAFT]: { label: 'Brouillon', bg: 'bg-slate-100', text: 'text-slate-600' },
  [CompetitionStatus.REGISTRATION_OPEN]: {
    label: 'Inscriptions ouvertes',
    bg: 'bg-blue-100',
    text: 'text-blue-700',
  },
  [CompetitionStatus.REGISTRATION_CLOSED]: {
    label: 'Inscriptions fermées',
    bg: 'bg-amber-100',
    text: 'text-amber-700',
  },
  [CompetitionStatus.IN_PROGRESS]: { label: 'En cours', bg: 'bg-emerald-100', text: 'text-emerald-700' },
  [CompetitionStatus.FINISHED]: { label: 'Terminé', bg: 'bg-slate-100', text: 'text-slate-500' },
  [CompetitionStatus.CANCELLED]: { label: 'Annulé', bg: 'bg-red-100', text: 'text-red-700' },
  [CompetitionStatus.DISABLED_BY_ADMIN]: { label: 'Désactivé', bg: 'bg-red-100', text: 'text-red-700' },
};
